/**
 * Un campo con su etiqueta, su ayuda y su error, cableados entre sí.
 *
 * El cableado ARIA (`aria-describedby` apuntando a la ayuda y al error,
 * `aria-invalid` cuando hay error) es justo lo que se olvida cuando cada
 * campo se arma a mano en cada pantalla. Acá se arma una vez y todos los
 * campos lo heredan.
 */
import type { ReactNode } from 'react';

export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
}

interface FieldProps {
  id: string;
  label: string;
  help?: string | undefined;
  error?: string | undefined;
  /** Texto «obligatorio» / «opcional» que se muestra junto a la etiqueta. */
  hint?: string | undefined;
  children: (props: FieldControlProps) => ReactNode;
}

export function Field({ id, label, help, error, hint, children }: FieldProps): JSX.Element {
  const helpId = help === undefined ? undefined : `${id}-help`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [helpId, errorId].filter((x): x is string => x !== undefined).join(' ');

  return (
    <div className={error === undefined ? 'field' : 'field invalid'}>
      <label htmlFor={id}>
        {label}
        {hint !== undefined && <span className="help"> ({hint})</span>}
      </label>
      {help !== undefined && (
        <span className="help" id={helpId}>
          {help}
        </span>
      )}
      {children({
        id,
        'aria-describedby': describedBy === '' ? undefined : describedBy,
        'aria-invalid': error === undefined ? undefined : true,
      })}
      {error !== undefined && (
        <span className="error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
